/**
 * pages-sku-profit.ts — หน้า "SKU ตัวทำเงิน / ตัวขาดทุน" (Simple Mode)
 *
 * ดึงกำไรราย SKU จาก `/api/pnl/sku-profit` (live) แล้วจัดอันดับ:
 * - ตัวทำเงิน = netContribution บวกสูงสุด
 * - ตัวขาดทุน = netContribution ติดลบ/ต่ำสุด
 * ยอดรวมสอดคล้องกับบรรทัด Sales/Cost of products ของ P&L — ไม่มี engine ใหม่
 *
 * demo mode: ไม่มี API → แสดงคำแนะนำให้เข้าสู่ระบบข้อมูลจริง
 */
import type { Page } from './router.js';
import { getTenant } from './state.js';
import { getFilters, setFilters, presetRange } from './filters.js';
import { formatTHB, fmtPct } from './format.js';
import { esc } from './ui.js';
import * as api from './api.js';

/** แถบเลือกช่วงแบบง่าย (เหมือนหน้ากำไรวันนี้) */
function rangeChips(): string {
  const presets: [string, string][] = [
    ['today', 'วันนี้'],
    ['last7', '7 วัน'],
    ['this_month', 'เดือนนี้'],
    ['prev_month', 'เดือนก่อน'],
  ];
  return `<div class="range-chips">${presets
    .map(([k, l]) => `<button type="button" class="chip-range" data-preset="${k}">${l}</button>`)
    .join('')}</div>`;
}

function moneyNum(v: string): string {
  return formatTHB(v).replace('฿', '');
}

/** การ์ดอันดับ (ตัวทำเงิน/ตัวขาดทุน) พร้อมแถบสัดส่วน */
function rankCard(title: string, note: string, rows: api.SkuProfitRow[], positive: boolean): string {
  if (rows.length === 0)
    return `<div class="card"><h3>${esc(title)}</h3><p class="muted">ยังไม่มีข้อมูลในช่วงนี้</p></div>`;
  const max = Math.max(...rows.map((r) => Math.abs(Number(r.netContribution))), 1);
  const bars = rows
    .map((r) => {
      const n = Number(r.netContribution);
      const w = (Math.abs(n) / max) * 100;
      const missing = r.missingCost
        ? ' <span class="warn-text" title="ยังไม่มีต้นทุน — กำไรยังไม่สมบูรณ์">⚠️ ไม่มีต้นทุน</span>'
        : '';
      return `<div class="eat-bar">
        <div class="eat-lbl"><span>${esc(r.skuName)}${missing}</span><span class="num ${positive ? 'pos' : 'neg'}">${n >= 0 ? '+' : ''}${moneyNum(r.netContribution)}</span></div>
        <div class="eat-track"><div class="eat-fill${positive ? '' : ' neg'}" style="width:${w.toFixed(1)}%"></div></div>
        <div class="eat-sub muted">ขาย ${esc(r.unitsSold)} ชิ้น · รายได้ ${moneyNum(r.revenue)} · กำไร ${fmtPct(r.marginPct)}</div>
      </div>`;
    })
    .join('');
  return `<div class="card"><h3>${esc(title)} <span class="muted">// ${esc(note)}</span></h3>${bars}</div>`;
}

/** ตารางเต็มทุก SKU (เรียงกำไรมาก→น้อย) */
function fullTable(rows: api.SkuProfitRow[]): string {
  if (rows.length === 0) return '<p class="muted">ยังไม่มีข้อมูล</p>';
  const body = rows
    .map((r) => {
      const net = Number(r.netContribution);
      return `<tr class="${r.missingCost ? 'row-warn' : ''}">
        <td>${esc(r.skuName)}${r.missingCost ? ' <span class="warn-text">⚠️</span>' : ''}</td>
        <td class="num">${esc(r.unitsSold)}</td>
        <td class="num">${moneyNum(r.revenue)}</td>
        <td class="num">${moneyNum(r.cogs)}</td>
        <td class="num">${moneyNum(r.platformFees)}</td>
        <td class="num ${net < 0 ? 'neg-num' : ''}">${moneyNum(r.netContribution)}</td>
        <td class="num">${fmtPct(r.marginPct)}</td>
      </tr>`;
    })
    .join('');
  return `<div class="table-scroll"><table class="data">
    <thead><tr><th>SKU</th><th>ขาย (ชิ้น)</th><th>รายได้</th><th>ต้นทุน</th><th>ค่าธรรมเนียม</th><th>กำไรสุทธิ</th><th>กำไร %</th></tr></thead>
    <tbody>${body}</tbody></table></div>`;
}

export const skuProfitPage: Page = {
  title: 'ตัวทำเงิน / ตัวขาดทุน',
  render() {
    return `
      <div class="topbar"><h1>ตัวทำเงิน / ตัวขาดทุน</h1>${rangeChips()}</div>
      <p class="foot-note">จัดอันดับ SKU ตาม "กำไรสุทธิ" (รายได้ − ต้นทุน − ค่าธรรมเนียมแพลตฟอร์มที่ปันส่วน) ในช่วงที่เลือก</p>
      <div id="sku-profit-body"><p class="muted">กำลังโหลด…</p></div>`;
  },
  mount(root) {
    root.querySelectorAll<HTMLButtonElement>('.chip-range[data-preset]').forEach((b) =>
      b.addEventListener('click', () => setFilters(presetRange(b.dataset['preset']!))),
    );
    const body = root.querySelector<HTMLElement>('#sku-profit-body')!;
    if (!getTenant()) {
      body.innerHTML =
        '<div class="card"><p class="muted">หน้านี้ใช้ได้ในโหมดข้อมูลจริง — เข้าสู่ระบบด้วยบัญชี Supabase เพื่อดูกำไรราย SKU จากออเดอร์จริง</p></div>';
      return;
    }
    const f = getFilters();
    void api
      .getSkuProfit(f.from, f.to)
      .then((rep) => {
        const makers = rep.rows.filter((r) => Number(r.netContribution) > 0).slice(0, 5);
        const losers = rep.rows
          .filter((r) => Number(r.netContribution) < 0)
          .slice(-5)
          .reverse();
        const banner = rep.missingCostCount
          ? `<div class="banner"><span>⚠️</span><div><strong>${rep.missingCostCount} SKU ยังไม่มีต้นทุน</strong> — กำไรของ SKU เหล่านั้นยังไม่สมบูรณ์ (กรอกต้นทุนที่หน้า "สินค้าและต้นทุน SKU")</div></div>`
          : '';
        body.innerHTML =
          banner +
          `<div class="today-grid">
            ${rankCard('🏆 ตัวทำเงิน', 'กำไรสูงสุด', makers, true)}
            ${rankCard('🩸 ตัวขาดทุน', 'ขาดทุน/กำไรต่ำสุด', losers, false)}
          </div>
          <div class="card"><h3>กำไรราย SKU ทั้งหมด</h3>${fullTable(rep.rows)}
            <p class="foot-note">รวมกำไรสุทธิ ${moneyNum(rep.totals.netContribution)} · รายได้ ${moneyNum(rep.totals.revenue)} · ค่าธรรมเนียม ${moneyNum(rep.totals.platformFees)}</p>
          </div>`;
      })
      .catch((e: unknown) => {
        body.innerHTML = `<div class="card"><p class="error">${esc(
          e instanceof Error ? e.message : 'โหลดกำไรราย SKU ไม่สำเร็จ',
        )}</p></div>`;
      });
  },
};
